import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';

import { heartbeat, heartbeatTrainTogether } from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';

import Constants from '@/constants';

import { useHeartbeatSuspension } from './useHeartbeatSuspension';

import { shouldSkipHeartbeatErrorReporting } from '@/routes/TrainingPlans/hooks/heartbeat-error';

/**
 * Hook to track time spent on a training plan step by sending periodic heartbeats to the API.
 *
 * Architecture:
 * - Uses two intervals: one for tracking elapsed time (POLL_TIME_ROUNDUP), one for sending heartbeats (POLL_TIME_DEFAULT)
 * - When the timer stops, sends a final "round up" heartbeat for any elapsed time since the last heartbeat
 * - Uses refs to track elapsed time and last valid stepId to ensure final heartbeat is sent correctly
 *
 * The lastStepIdRef is needed because when a step is completed, the stepId prop may become empty
 * before the cleanup function runs. We store the last valid stepId so we can still send the final heartbeat.
 *
 * @see PATHWAY-2935 - Fixed timer to correctly track and display time spent on steps
 */
export const useTrainingHeartbeatPlanTimer = (
  planId: string,
  stepId: string,
  userIds: string[],
  stepStatus: string,
) => {
  const [timerIsRunning, setTimerIsRunning] = useState(false);

  // Track elapsed time since last heartbeat for final "round up" on stop
  const elapsedSinceLastHeartbeatRef = useRef(0);
  // Store last valid stepId so we can send final heartbeat when timer stops
  const lastStepIdRef = useRef(stepId);

  const { data: auth } = useAuth();
  const userId = auth?.userId ?? '';

  const isSuspended = useHeartbeatSuspension(timerIsRunning);

  // Update the ref whenever we get a valid stepId
  useEffect(() => {
    lastStepIdRef.current = stepId;
  }, [stepId]);

  const sendHeartbeat = useEffectEvent((milliSeconds: number) => {
    const seconds = milliSeconds / 1000;
    const isTrainTogether =
      userIds.length > 1 || (userIds.length === 1 && userIds[0] !== userId);
    const currentStepId = lastStepIdRef.current;

    if (!currentStepId || seconds <= 0) {
      return;
    }

    const request = isTrainTogether
      ? heartbeatTrainTogether({
          path: { planId, stepId: currentStepId },
          body: { intervalInSeconds: seconds, userIds },
        })
      : heartbeat({
          path: { planId, stepId: currentStepId },
          body: { intervalInSeconds: seconds },
        });

    request.catch((err: unknown) => {
      if (shouldSkipHeartbeatErrorReporting(err)) {
        return;
      }
      console.error('[useTrainingHeartbeatPlanTimer] Heartbeat error:', err);
      datadogRum.addError(err);
    });
  });

  useEffect(() => {
    const { POLL_TIME_DEFAULT, POLL_TIME_ROUNDUP } =
      Constants.VIEW_TIME_DEFAULTS;

    let roundUpIntervalId: ReturnType<typeof setInterval> | undefined;
    let heartbeatIntervalId: ReturnType<typeof setInterval> | undefined;

    // Only set up intervals if timer is running, step is valid, and not suspended
    if (timerIsRunning && stepStatus && !isSuspended) {
      // Track elapsed time for round-up on stop
      roundUpIntervalId = setInterval(() => {
        elapsedSinceLastHeartbeatRef.current += POLL_TIME_ROUNDUP;
      }, POLL_TIME_ROUNDUP);

      // Send heartbeat at regular intervals
      heartbeatIntervalId = setInterval(() => {
        sendHeartbeat(POLL_TIME_DEFAULT);
        elapsedSinceLastHeartbeatRef.current = 0;
      }, POLL_TIME_DEFAULT);
    }

    return () => {
      // Clear intervals (clearInterval safely handles undefined)
      clearInterval(roundUpIntervalId);
      clearInterval(heartbeatIntervalId);

      // Send final heartbeat for elapsed time when timer stops
      if (elapsedSinceLastHeartbeatRef.current > 0 && lastStepIdRef.current) {
        sendHeartbeat(elapsedSinceLastHeartbeatRef.current);
      }
      elapsedSinceLastHeartbeatRef.current = 0;
    };
  }, [timerIsRunning, stepStatus, isSuspended]);

  const startTimer = () => {
    setTimerIsRunning(true);
  };
  const stopTimer = () => {
    setTimerIsRunning(false);
  };

  return [startTimer, stopTimer] as const;
};

export default useTrainingHeartbeatPlanTimer;
